import type { ExportProgress } from './types';

const IMAGE_TIMEOUT_MS = 15000;

export function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException('Export cancelled', 'AbortError');
}

export function abortable<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  throwIfAborted(signal);
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('Export cancelled', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    promise.then(
      (value) => { signal.removeEventListener('abort', abort); resolve(value); },
      (error: unknown) => { signal.removeEventListener('abort', abort); reject(error); },
    );
  });
}

function rasterMime(bytes: Uint8Array): string {
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47
    && bytes[4] === 13 && bytes[5] === 10 && bytes[6] === 26 && bytes[7] === 10) return 'image/png';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  const header = String.fromCharCode(...Array.from(bytes.slice(0, 12)));
  if (header.startsWith('GIF87a') || header.startsWith('GIF89a')) return 'image/gif';
  if (header.startsWith('RIFF') && header.slice(8, 12) === 'WEBP') return 'image/webp';
  throw new Error('Unsupported image');
}

function readDataUrl(blob: Blob, signal: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    const abort = () => reader.abort();
    const cleanup = () => signal.removeEventListener('abort', abort);
    reader.onload = () => {
      cleanup();
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Invalid image data'));
    };
    reader.onerror = () => { cleanup(); reject(new Error('Image read failed')); };
    reader.onabort = () => { cleanup(); reject(new DOMException('Export cancelled', 'AbortError')); };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) {
      cleanup();
      reject(new DOMException('Export cancelled', 'AbortError'));
    } else reader.readAsDataURL(blob);
  });
}

export async function decodeImage(source: string, signal: AbortSignal, owner: Window = window): Promise<void> {
  throwIfAborted(signal);
  const image = owner.document.createElement('img');
  await new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      signal.removeEventListener('abort', abort);
      image.onload = null;
      image.onerror = null;
    };
    const abort = () => {
      cleanup();
      image.removeAttribute('src');
      reject(new DOMException('Export cancelled', 'AbortError'));
    };
    image.onload = () => {
      cleanup();
      if (image.naturalWidth > 0 && image.naturalHeight > 0) resolve();
      else reject(new Error('Invalid image dimensions'));
    };
    image.onerror = () => { cleanup(); reject(new Error('Image decode failed')); };
    signal.addEventListener('abort', abort, { once: true });
    image.src = source;
  });
}

async function loadImage(source: string, signal: AbortSignal): Promise<string> {
  const url = new URL(source, window.location.href);
  if (!['http:', 'https:', 'data:', 'blob:'].includes(url.protocol) || url.username || url.password) {
    throw new Error('Unsupported image source');
  }
  const response = await fetch(url.href, {
    signal,
    credentials: url.origin === window.location.origin ? 'same-origin' : 'omit',
    mode: 'cors',
    referrerPolicy: 'no-referrer',
  });
  if (!response.ok) throw new Error('Image request failed');
  const bytes = await response.arrayBuffer();
  throwIfAborted(signal);
  const mime = rasterMime(new Uint8Array(bytes));
  const data = await readDataUrl(new window.Blob([bytes], { type: mime }), signal);
  await decodeImage(data, signal);
  return data;
}

export async function embedImages(
  root: DocumentFragment,
  signal: AbortSignal,
  missingLabel: string,
  onProgress: (progress: ExportProgress) => void,
): Promise<string[]> {
  const images = Array.from(root.querySelectorAll('img'));
  const sources = Array.from(new Set(images.map((image) => image.getAttribute('src') ?? '')));
  const results = new Map<string, string | null>();
  let next = 0;
  let done = 0;
  onProgress({ done, total: sources.length });
  const worker = async () => {
    while (next < sources.length) {
      throwIfAborted(signal);
      const source = sources[next++];
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = window.setTimeout(abort, IMAGE_TIMEOUT_MS);
      try {
        if (!source) throw new Error('Missing image source');
        results.set(source, await abortable(loadImage(source, controller.signal), controller.signal));
      } catch {
        throwIfAborted(signal);
        results.set(source, null);
      } finally {
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
      onProgress({ done: ++done, total: sources.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(3, sources.length) }, worker));
  throwIfAborted(signal);
  const missing: string[] = [];
  images.forEach((image, index) => {
    const embedded = results.get(image.getAttribute('src') ?? '');
    if (embedded) {
      image.setAttribute('src', embedded);
      return;
    }
    const label = image.getAttribute('alt')?.trim() || `#${index + 1}`;
    missing.push(label);
    const placeholder = document.createElement('span');
    placeholder.className = 'missing-image';
    placeholder.textContent = `${missingLabel}: ${label}`;
    image.replaceWith(placeholder);
  });
  return missing;
}
